import {
  AutoAwesome,
  CheckCircle,
  Close,
  FlightTakeoff,
  HeadsetMic,
  Hub,
  Insights,
  Lock,
  ManageAccounts,
  Menu as MenuIcon,
  QueryStats,
  Restaurant,
  RocketLaunch,
  ShoppingBag,
  SupportAgent,
  Tune,
  WhatsApp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { createLyproTheme } from "../theme";
import { useLogin } from "./hooks/useLogin";

const DEMO_WHATSAPP_NUMBER = "573045643425";
const COOKIE_STORAGE_KEY = "lypro_cookie_consent_v1";
const loginTheme = createLyproTheme("dark");

type Integration = {
  name: string;
  href: string;
  logoSrc: string;
};

const integrations: Integration[] = [
  {
    name: "Mapos",
    href: "https://maposapp.com",
    logoSrc: "/assets/integrations/mapos-logo.png",
  },
];

const maposIntegration = integrations[0]!;

const sectors = [
  {
    title: "Restaurantes",
    description:
      "Toma pedidos, confirma pagos, agenda reservas y escala a un humano cuando la conversación lo necesita.",
    icon: <Restaurant />,
    accent: "#38e6c5",
  },
  {
    title: "Agencias de viajes",
    description:
      "Responde cotizaciónes, clasifica intención de compra y acompaña el seguimiento sin perder contexto.",
    icon: <FlightTakeoff />,
    accent: "#8fc7ff",
  },
  {
    title: "Ventas personalizadas",
    description:
      "Califica leads, recomienda productos y mantiene cada conversación conectada con la operación comercial.",
    icon: <ShoppingBag />,
    accent: "#ffbc73",
  },
];

const features = [
  {
    title: "Atención 24/7 por WhatsApp",
    description:
      "Tus clientes reciben respuestas rápidas, claras y alíneadas con tus reglas de negocio.",
    icon: <WhatsApp />,
  },
  {
    title: "Operación conectada",
    description:
      "La IA trabaja con menús, pedidos, reservas, CRM e integraciones para cerrar ciclos completos.",
    icon: <Hub />,
  },
  {
    title: "Escalamiento humano",
    description:
      "Cuando hace falta criterio del equipo, LyPro entrega el contexto y deja la conversación lista.",
    icon: <HeadsetMic />,
  },
  {
    title: "Medición comercial",
    description:
      "Analíticas de chats, ventas, tiempos y comportamiento para decidir con datos frescos.",
    icon: <QueryStats />,
  },
];

const workflow = [
  "Entrenamos la IA con tus procesos, productos, horarios y tono de marca.",
  "Conectamos canales e integraciones para que cada dato llegue donde debe.",
  "Monitoreas conversaciones, métricas y ajustes desde el panel privado.",
];

type CookiePreferences = {
  analytics: boolean;
  personalization: boolean;
  marketing: boolean;
};

const defaultCookiePreferences: CookiePreferences = {
  analytics: false,
  personalization: false,
  marketing: false,
};

const buildDemoHref = () => {
  if (!DEMO_WHATSAPP_NUMBER) return "#contacto";

  const message = encodeURIComponent(
    "Hola, quiero agendar una demo de LyPro AI."
  );
  return `https://wa.me/${DEMO_WHATSAPP_NUMBER}?text=${message}`;
};

const sectionSx = {
  width: "100%",
  boxSizing: "border-box",
  px: { xs: 2, md: 5 },
  py: { xs: 7, md: 10 },
};

const sectionInnerSx = {
  width: "100%",
  boxSizing: "border-box",
  maxWidth: { xs: "calc(100vw - 32px)", md: 1180 },
  mx: "auto",
};

const darkLoginTextFieldSx = {
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.62)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#8ef4de",
  },
  "& .MuiOutlinedInput-root": {
    bgcolor: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.94)",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.16)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(143,199,255,0.34)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#38e6c5",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "#ff9a9a",
  },
} as const;

const IntegrationLogo = ({ integration }: { integration: Integration }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = Boolean(integration.logoSrc) && !logoFailed;

  if (showLogo) {
    return (
      <Box
        component="img"
        src={integration.logoSrc}
        alt={`${integration.name} logo`}
        onError={() => setLogoFailed(true)}
        sx={{
          maxWidth: "100%",
          width: "100%",
          height: "100%",
          maxHeight: 64,
          objectFit: "contain",
          transform: "scale(1.12)",
        }}
      />
    );
  }

  return (
    <Typography
      variant="body2"
      align="center"
      sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 900 }}
    >
      {integration.name}
    </Typography>
  );
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(
    defaultCookiePreferences
  );

  useEffect(() => {
    try {
      const storedConsent = window.localStorage.getItem(COOKIE_STORAGE_KEY);
      setIsVisible(!storedConsent);

      if (storedConsent) {
        const parsed = JSON.parse(storedConsent) as CookiePreferences;
        setPreferences({ ...defaultCookiePreferences, ...parsed });
      }
    } catch {
      setIsVisible(true);
    }
  }, []);

  const savePreferences = (nextPreferences: CookiePreferences) => {
    const payload = {
      necessary: true,
      ...nextPreferences,
      savedAt: new Date().toISOString(),
    };

    window.localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(payload));
    setPreferences(nextPreferences);
    setIsVisible(false);
    setIsSettingsOpen(false);
  };

  const acceptAll = () => {
    savePreferences({
      analytics: true,
      personalization: true,
      marketing: true,
    });
  };

  const rejectOptional = () => {
    savePreferences(defaultCookiePreferences);
  };

  const togglePreference =
    (key: keyof CookiePreferences) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPreferences((current) => ({
        ...current,
        [key]: event.target.checked,
      }));
    };

  if (!isVisible) return null;

  return (
    <>
      <Paper
        elevation={16}
        sx={{
          position: "fixed",
          left: { xs: 16, md: 24 },
          right: { xs: 16, md: "auto" },
          bottom: 18,
          zIndex: 20,
          width: { xs: "auto", md: 560 },
          maxWidth: "calc(100vw - 32px)",
          boxSizing: "border-box",
          borderRadius: 2,
          border: "1px solid rgba(56,230,197,0.28)",
          bgcolor: "rgba(9, 13, 26, 0.96)",
          backdropFilter: "blur(18px)",
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Lock sx={{ color: "#38e6c5", mt: 0.3 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Preferencias de cookies
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Usamos cookies necesarias para que la página funcione. Puedes
                activar analíticas, personalización y marketing cuando quieras.
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Button variant="contained" onClick={acceptAll} fullWidth>
              Aceptar todas
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsSettingsOpen(true)}
              fullWidth
            >
              Configurar
            </Button>
            <Button color="inherit" onClick={rejectOptional} fullWidth>
              Rechazar opcionales
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: "1px solid rgba(143,199,255,0.22)",
            bgcolor: "#111725",
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Configurar cookies
          <IconButton
            aria-label="Cerrar configuración de cookies"
            onClick={() => setIsSettingsOpen(false)}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Puedes cambiar estas preferencias más adelante limpiando el
              almacenamiento del navegador. No cargamos trackers desde este
              panel; queda preparado para futuras integraciones.
            </Typography>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Necesarias
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Siempre activas. Permiten seguridad, navegación y preferencias
                básicas del sitio.
              </Typography>
            </Box>

            <Divider />

            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.analytics}
                  onChange={togglePreference("analytics")}
                />
              }
              label="Analíticas"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.personalization}
                  onChange={togglePreference("personalization")}
                />
              }
              label="Personalización"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences.marketing}
                  onChange={togglePreference("marketing")}
                />
              }
              label="Marketing"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button color="inherit" onClick={rejectOptional}>
            Rechazar opcionales
          </Button>
          <Button
            variant="contained"
            onClick={() => savePreferences(preferences)}
          >
            Guardar preferencias
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const LoginPage: React.FC = () => {
  const {
    user,
    email,
    password,
    error,
    isSubmitting,
    backgroundImage,
    isEmailValid,
    isPasswordValid,
    isFormValid,
    setEmail,
    setPassword,
    handleLogin,
  } = useLogin();

  const demoHref = useMemo(buildDemoHref, []);
  const demoTarget = DEMO_WHATSAPP_NUMBER ? "_blank" : undefined;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const closeMobileNav = () => setMobileNavOpen(false);

  const handleMobileNavLinkClick = () => {
    closeMobileNav();
  };

  if (user) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <ThemeProvider theme={loginTheme}>
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#050814",
        color: "rgba(255,255,255,0.92)",
        overflowX: "hidden",
      }}
    >
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          bgcolor: "rgba(5, 8, 20, 0.78)",
          backdropFilter: "blur(18px)",
        }}
      >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              ...sectionInnerSx,
              px: { xs: 2, md: 5 },
              py: 1.5,
              gap: 1.5,
            }}
          >
            <Box
              component="img"
              src="/assets/icons/ic-logo-light.png"
            alt="LyPro AI"
            sx={{
              width: { xs: 118, sm: 144 },
              height: 42,
              objectFit: "contain",
              objectPosition: "left center",
              filter: "drop-shadow(0 10px 26px rgba(0,0,0,0.34))",
              }}
            />

            <IconButton
              aria-label="Abrir menú de navegación"
              onClick={() => setMobileNavOpen(true)}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                ml: "auto",
                color: "rgba(255,255,255,0.88)",
                border: "1px solid rgba(255,255,255,0.12)",
                bgcolor: "rgba(255,255,255,0.04)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
              }}
            >
              <MenuIcon />
            </IconButton>

          <Stack
            component="nav"
            direction="row"
            spacing={3}
              sx={{
              display: { xs: "none", md: "flex" },
              color: "rgba(255,255,255,0.72)",
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            <Box component="a" href="#integraciones" sx={{ color: "inherit" }}>
              Integraciones
            </Box>
            <Box component="a" href="#que-hacemos" sx={{ color: "inherit" }}>
              Qué hacemos
            </Box>
            <Box component="a" href="#sectores" sx={{ color: "inherit" }}>
              Sectores
            </Box>
          </Stack>

          <Button
            component="a"
            href={demoHref}
              target={demoTarget}
              rel={demoTarget ? "noreferrer" : undefined}
              variant="contained"
              startIcon={<WhatsApp />}
              sx={{
              display: { xs: "none", sm: "inline-flex" },
              bgcolor: "#38e6c5",
              color: "#041014",
              fontWeight: 900,
              "&:hover": { bgcolor: "#23c9aa" },
              }}
            >
              Agendar demo
            </Button>
          </Stack>
        </Box>

      <Drawer
        anchor="right"
        open={mobileNavOpen}
        onClose={closeMobileNav}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: "min(86vw, 320px)",
            boxSizing: "border-box",
            bgcolor: "rgba(8, 12, 24, 0.98)",
            color: "white",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            backdropFilter: "blur(18px)",
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2.5 }}
          >
            <Box
              component="img"
              src="/assets/icons/ic-logo-light.png"
              alt="LyPro AI"
              sx={{
                width: 116,
                height: 40,
                objectFit: "contain",
                objectPosition: "left center",
              }}
            />

            <IconButton
              aria-label="Cerrar menú de navegación"
              onClick={closeMobileNav}
              sx={{ color: "rgba(255,255,255,0.88)" }}
            >
              <Close />
            </IconButton>
          </Stack>

          <List sx={{ p: 0 }}>
            <ListItemButton
              component="a"
              href="#integraciones"
              onClick={handleMobileNavLinkClick}
              sx={{
                mb: 1,
                borderRadius: 2,
                color: "rgba(255,255,255,0.88)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <ListItemText primary="Integraciones" />
            </ListItemButton>
            <ListItemButton
              component="a"
              href="#que-hacemos"
              onClick={handleMobileNavLinkClick}
              sx={{
                mb: 1,
                borderRadius: 2,
                color: "rgba(255,255,255,0.88)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <ListItemText primary="Qué hacemos" />
            </ListItemButton>
            <ListItemButton
              component="a"
              href="#sectores"
              onClick={handleMobileNavLinkClick}
              sx={{
                borderRadius: 2,
                color: "rgba(255,255,255,0.88)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <ListItemText primary="Sectores" />
            </ListItemButton>
          </List>

          <Button
            component="a"
            href={demoHref}
            target={demoTarget}
            rel={demoTarget ? "noreferrer" : undefined}
            variant="contained"
            fullWidth
            startIcon={<WhatsApp />}
            onClick={closeMobileNav}
            sx={{
              mt: 2.5,
              minHeight: 48,
              bgcolor: "#38e6c5",
              color: "#041014",
              fontWeight: 900,
              "&:hover": { bgcolor: "#23c9aa" },
            }}
          >
            Agendar demo
          </Button>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          position: "relative",
          isolation: "isolate",
          pt: { xs: 11, md: 13 },
          backgroundImage: `linear-gradient(90deg, rgba(5,8,20,0.98) 0%, rgba(5,8,20,0.92) 38%, rgba(5,8,20,0.72) 100%), url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundAttachment: { xs: "scroll", md: "fixed" },
          "& > :not(.login-background-fade)": {
            position: "relative",
            zIndex: 1,
          },
        }}
      >
        <Box
          className="login-background-fade"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(180deg, rgba(5,8,20,0) 0%, #050814 23%, #050814 100%)",
            top: "72vh",
          }}
        />

        <Box
          sx={{
            ...sectionSx,
            pt: { xs: 5, md: 8 },
            pb: { xs: 7, md: 11 },
            position: "relative",
          }}
        >
          <Stack
            sx={sectionInnerSx}
            direction={{ xs: "column", lg: "row" }}
            spacing={{ xs: 5, lg: 7 }}
            alignItems="center"
          >
            <Stack
              spacing={3.2}
              sx={{
                flex: 1,
                minWidth: 0,
                width: "100%",
                maxWidth: { xs: "calc(100vw - 32px)", lg: 680 },
              }}
            >
              <Chip
                icon={<AutoAwesome />}
                label="Agentes IA para conversación, venta y operación"
                sx={{
                  width: "fit-content",
                  maxWidth: "100%",
                  height: "auto",
                  borderColor: "rgba(56,230,197,0.46)",
                  color: "#c9fff4",
                  bgcolor: "rgba(56,230,197,0.1)",
                  fontWeight: 800,
                  "& .MuiChip-label": {
                    display: "block",
                    whiteSpace: "normal",
                    overflow: "visible",
                    textOverflow: "clip",
                    py: 0.45,
                  },
                }}
                variant="outlined"
              />

              <Box>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: 44, sm: 58, md: 76 },
                    lineHeight: 0.95,
                    fontWeight: 900,
                    letterSpacing: 0,
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                  }}
                >
                  LyPro AI
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    mt: 2,
                    width: "100%",
                    maxWidth: "100%",
                    fontSize: { xs: 21, md: 28 },
                    lineHeight: 1.18,
                    color: "rgba(255,255,255,0.9)",
                    fontWeight: 800,
                  }}
                >
                  Automatiza atención, pedidos y ventas por WhatsApp con IA que
                  entiende tu negocio.
                </Typography>
              </Box>

              <Typography
                sx={{
                  maxWidth: 640,
                  width: "100%",
                  color: "rgba(255,255,255,0.68)",
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: 1.75,
                }}
              >
                Creamos asistentes digitales para restaurantes, agencias de
                viajes y ventas personalizadas. Responden rápido, operan con tus
                reglas y dejan el contexto listo para tu equipo.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  component="a"
                  href={demoHref}
                  target={demoTarget}
                  rel={demoTarget ? "noreferrer" : undefined}
                  variant="contained"
                  size="large"
                  startIcon={<WhatsApp />}
                  sx={{
                    minHeight: 48,
                    width: { xs: "100%", sm: "auto" },
                    bgcolor: "#38e6c5",
                    color: "#041014",
                    fontWeight: 900,
                    "&:hover": { bgcolor: "#23c9aa" },
                  }}
                >
                  Agendar demo
                </Button>
                <Button
                  component="a"
                  href="#que-hacemos"
                  variant="outlined"
                  color="inherit"
                  size="large"
                  startIcon={<Insights />}
                  sx={{ minHeight: 48, width: { xs: "100%", sm: "auto" } }}
                >
                  Funcionamiento
                </Button>
              </Stack>

              <Stack direction="row" flexWrap="wrap" gap={1}>
                {["WhatsApp", "Pedidos", "Reservas", "CRM", "Analíticas"].map(
                  (item) => (
                    <Chip
                      key={item}
                      label={item}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "rgba(255,255,255,0.78)",
                        fontWeight: 700,
                      }}
                    />
                  )
                )}
              </Stack>
            </Stack>

            <Paper
              elevation={20}
              sx={{
                width: "100%",
                maxWidth: 430,
                boxSizing: "border-box",
                borderRadius: 2,
                p: { xs: 2.5, sm: 3.2 },
                border: "1px solid rgba(143,199,255,0.22)",
                bgcolor: "rgba(12, 17, 31, 0.92)",
                backdropFilter: "blur(18px)",
                color: "rgba(255,255,255,0.92)",
              }}
            >
              <Stack spacing={2.4}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>
                    Ingresar al panel
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, color: "rgba(255,255,255,0.62)" }}
                  >
                    Gestiona conversaciones, configuración y analíticas de tus
                    asistentes IA.
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleLogin}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Correo"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={!!email && !isEmailValid}
                      helperText={email && !isEmailValid ? "Correo inválido" : ""}
                      autoComplete="email"
                      sx={darkLoginTextFieldSx}
                    />
                    <TextField
                      fullWidth
                      label="Contraseña"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={!!password && !isPasswordValid}
                      helperText={
                        password && !isPasswordValid
                          ? "Debe tener al menos 6 caracteres"
                          : ""
                      }
                      autoComplete="current-password"
                      sx={darkLoginTextFieldSx}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                      disabled={!isFormValid || isSubmitting}
                      sx={{
                        minHeight: 48,
                        fontWeight: 900,
                        bgcolor: "#38e6c5",
                        color: "#041014",
                        "&:hover": { bgcolor: "#23c9aa" },
                        "&.Mui-disabled": {
                          bgcolor: "rgba(255,255,255,0.08)",
                          color: "rgba(255,255,255,0.32)",
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <span translate="no">Ingresar</span>
                      )}
                    </Button>
                  </Stack>
                </Box>

                {error && (
                  <Typography color="error" align="center" variant="body2">
                    {error}
                  </Typography>
                )}

                <Divider />

                <Stack direction="row" spacing={1.2} alignItems="center">
                  <CheckCircle sx={{ color: "#38e6c5", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.62)" }}>
                    Acceso seguro para equipos autorizados de LyPro.
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Box>        <Box
          component="section"
          id="integraciones"
          sx={{
            ...sectionSx,
            py: { xs: 5, md: 7 },
          }}
        >
          <Stack sx={sectionInnerSx} spacing={3}>
            <Typography
              variant="overline"
              sx={{ color: "#38e6c5", fontWeight: 900, letterSpacing: 0 }}
            >
              Integraciones
            </Typography>

            <Paper
              component="a"
              href={maposIntegration.href}
              target="_blank"
              rel="noreferrer"
              elevation={0}
              sx={{
                position: "relative",
                overflow: "hidden",
                p: { xs: 2.5, md: 3.5 },
                borderRadius: 3,
                color: "inherit",
                textDecoration: "none",
                border: "1px solid rgba(255,255,255,0.12)",
                bgcolor: "rgba(8,10,24,0.78)",
                boxShadow: "0 24px 70px rgba(0,0,0,0.38)",
                backdropFilter: "blur(14px)",
                maxWidth: 760,
                transition: "transform 160ms ease, border-color 160ms ease",
                '&::before': {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(135deg, rgba(56,230,197,0.16), rgba(143,199,255,0.07) 52%, rgba(255,255,255,0.02) 100%)",
                  pointerEvents: "none",
                },
                '&:hover': {
                  transform: "translateY(-1px)",
                  borderColor: "rgba(56,230,197,0.45)",
                },
              }}
            >
              <Stack spacing={2.5} sx={{ position: "relative" }}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                >
                  <Chip
                    label="Integración activa"
                    sx={{
                      bgcolor: "rgba(56,230,197,0.14)",
                      color: "#8ef4de",
                      fontWeight: 800,
                    }}
                  />
                  <Chip
                    label="MAPOS "
                    variant="outlined"
                    sx={{
                      borderColor: "rgba(255,255,255,0.16)",
                      color: "rgba(255,255,255,0.84)",
                      fontWeight: 800,
                    }}
                  />
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2.5}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Box
                    sx={{
                      width: 112,
                      height: 112,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      border: "1px solid rgba(255,255,255,0.15)",
                      bgcolor: "rgba(255,255,255,0.04)",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
                      flexShrink: 0,
                    }}
                  >
                    <IntegrationLogo integration={maposIntegration} />
                  </Box>

                  <Box sx={{ maxWidth: 560 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: "rgba(255,255,255,0.62)",
                        fontWeight: 900,
                        letterSpacing: 0.8,
                      }}
                    >
                      Partner POS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 900, mt: 0.5 }}>
                      MAPOS
                    </Typography>
                    <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.7)" }}>
                      Punto de partida para pedidos, clientes y seguimiento de la
                      operacion desde conversaciones.
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ color: "#8ef4de", width: "fit-content" }}
                >
                  <RocketLaunch sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 800 }}>Ir a Mapos</Typography>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Box>

        <Box component="section" id="que-hacemos" sx={sectionSx}>
          <Stack sx={sectionInnerSx} spacing={4}>
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                variant="overline"
                sx={{ color: "#8fc7ff", fontWeight: 900, letterSpacing: 0 }}
              >
                Qué hacemos
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 900 }}>
                Múltiples IA para convertir conversaciones en operación real.
              </Typography>
              <Typography
                sx={{ mt: 2, color: "rgba(255,255,255,0.68)", fontSize: 17 }}
              >
                LyPro combina asistentes conversaciónales, reglas de negocio e
                integraciones para que cada chat avance: responder, vender,
                reservar, registrar y medir.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {features.map((feature) => (
                <Paper
                  key={feature.title}
                  elevation={0}
                  sx={{
                    minHeight: 220,
                    borderRadius: 2,
                    p: 2.5,
                    border: "1px solid rgba(255,255,255,0.1)",
                    bgcolor: "rgba(255,255,255,0.055)",
                  }}
                >
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 1.5,
                        display: "grid",
                        placeItems: "center",
                        color: "#041014",
                        bgcolor: "#38e6c5",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Stack>
        </Box>

        <Box
          component="section"
          id="sectores"
          sx={{ ...sectionSx, bgcolor: "#08101f" }}
        >
          <Stack sx={sectionInnerSx} spacing={4}>
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                variant="overline"
                sx={{ color: "#ffbc73", fontWeight: 900, letterSpacing: 0 }}
              >
                Sectores top
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 900 }}>
                Donde LyPro ya tiene más impacto hoy.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {sectors.map((sector) => (
                <Paper
                  key={sector.title}
                  elevation={0}
                  sx={{
                    minHeight: 250,
                    borderRadius: 2,
                    p: 3,
                    border: "1px solid rgba(255,255,255,0.1)",
                    bgcolor: "rgba(255,255,255,0.055)",
                  }}
                >
                  <Stack spacing={2}>
                    <Box sx={{ color: sector.accent }}>{sector.icon}</Box>
                    <Typography variant="h5" sx={{ fontWeight: 900 }}>
                      {sector.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {sector.description}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Stack>
        </Box>

        <Box component="section" sx={sectionSx}>
          <Stack
            sx={sectionInnerSx}
            direction={{ xs: "column", md: "row" }}
            spacing={5}
            alignItems="flex-start"
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="overline"
                sx={{ color: "#38e6c5", fontWeight: 900, letterSpacing: 0 }}
              >
                Cómo opera la IA
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 900 }}>
                Diseñada para hablar bonito y ejecutar con orden.
              </Typography>
            </Box>
            <Stack spacing={2.2} sx={{ flex: 1.1 }}>
              {workflow.map((step, index) => (
                <Stack key={step} direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      flex: "0 0 36px",
                      height: 36,
                      borderRadius: 1,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(143,199,255,0.14)",
                      color: "#8fc7ff",
                      fontWeight: 900,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography sx={{ color: "rgba(255,255,255,0.78)", pt: 0.5 }}>
                    {step}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Box>

        <Box component="section" sx={sectionSx}>
          <Stack
            sx={sectionInnerSx}
            direction={{ xs: "column", md: "row" }}
            spacing={2}
          >
            {[
              {
                value: "24/7",
                label: "Disponibilidad para responder conversaciones entrantes.",
                icon: <SupportAgent />,
              },
              {
                value: "CRM",
                label: "Contexto centralizado para ventas, soporte y seguimiento.",
                icon: <ManageAccounts />,
              },
              {
                value: "IA + reglas",
                label: "Automatización con límites, pruebas y supervisión humana.",
                icon: <Tune />,
              },
            ].map((metric) => (
              <Paper
                key={metric.value}
                elevation={0}
                sx={{
                  flex: 1,
                  minHeight: 170,
                  borderRadius: 2,
                  p: 3,
                  border: "1px solid rgba(255,255,255,0.1)",
                  bgcolor: "rgba(255,255,255,0.055)",
                }}
              >
                <Stack spacing={1.5}>
                  <Box sx={{ color: "#38e6c5" }}>{metric.icon}</Box>
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    {metric.value}
                  </Typography>
                  <Typography color="text.secondary">{metric.label}</Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>

        <Box component="section" id="contacto" sx={sectionSx}>
          <Stack
            sx={sectionInnerSx}
            direction={{ xs: "column", md: "row" }}
            spacing={4}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ maxWidth: 720 }}>
              <Typography
                variant="overline"
                sx={{ color: "#38e6c5", fontWeight: 900, letterSpacing: 0 }}
              >
                Demo
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 900 }}>
                Hagamos que tu atención deje de depender del horario.
              </Typography>
              <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.68)" }}>
                Revisamos tus conversaciones actuales y diseñamos el primer
                flujo que puede atender, vender o escalar sin perder contexto.
              </Typography>
            </Box>
            <Button
              component="a"
              href={demoHref}
              target={demoTarget}
              rel={demoTarget ? "noreferrer" : undefined}
              variant="contained"
              size="large"
              startIcon={DEMO_WHATSAPP_NUMBER ? <WhatsApp /> : <RocketLaunch />}
              sx={{
                minHeight: 52,
                minWidth: { xs: "100%", sm: 210 },
                bgcolor: "#38e6c5",
                color: "#041014",
                fontWeight: 900,
                "&:hover": { bgcolor: "#23c9aa" },
              }}
            >
              Agendar demo
            </Button>
          </Stack>
        </Box>

        <Box
          component="footer"
          sx={{
            ...sectionSx,
            py: { xs: 3, md: 4 },
            borderTop: "1px solid rgba(255,255,255,0.08)",
            bgcolor: "rgba(5, 8, 20, 0.92)",
          }}
        >
          <Stack
            sx={sectionInnerSx}
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              LyPro AI
            </Typography>
            <Stack
              component="nav"
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 2.5 }}
            >
              <Box
                component={RouterLink}
                to="/privacy-policy"
                sx={{ color: "rgba(255,255,255,0.72)", fontWeight: 700 }}
              >
                Politica de privacidad
              </Box>
              <Box
                component={RouterLink}
                to="/terms"
                sx={{ color: "rgba(255,255,255,0.72)", fontWeight: 700 }}
              >
                Condiciones del servicio
              </Box>
              <Box
                component={RouterLink}
                to="/data-deletion"
                sx={{ color: "rgba(255,255,255,0.72)", fontWeight: 700 }}
              >
                Eliminacion de datos
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <CookieConsent />
    </Box>
    </ThemeProvider>
  );
};

export default LoginPage;
